import React, { useState } from 'react';
import { styled } from "@mui/material/styles";
import MuiDrawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Collapse from "@mui/material/Collapse";
import Chip from "@mui/material/Chip";

// Icons
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import DashboardIcon from "@mui/icons-material/Dashboard";
import HomeIcon from "@mui/icons-material/Home";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import SettingsIcon from '@mui/icons-material/Settings';

// Detection card icons
import PeopleIcon from "@mui/icons-material/People";
import PersonIcon from "@mui/icons-material/Person";
import SecurityIcon from "@mui/icons-material/Security";
import RouteIcon from "@mui/icons-material/Route";
import WarningIcon from "@mui/icons-material/Warning";

import { DRAWER_WIDTH, DETECTION_TYPES } from '../../utils/constants';
import { getCurrentToken } from '../../utils/authService';

const iconComponents = {
  PeopleIcon,
  PersonIcon,
  SecurityIcon,
  RouteIcon,
  WarningIcon,
};

// ─── Styled Components ────────────────────────────────────────────────────────

const StyledDrawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  "& .MuiDrawer-paper": {
    position: "relative",
    whiteSpace: "nowrap",
    width: DRAWER_WIDTH,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    boxSizing: "border-box",
    backgroundColor: "#17212b",
    color: "white",
    overflowX: "hidden",
    overflowY: "auto",
    ...(!open && {
      overflowX: "hidden",
      transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      width: theme.spacing(7),
      [theme.breakpoints.up("sm")]: {
        width: theme.spacing(9),
      },
    }),
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  color: "#6b7280",
  fontSize: "0.75rem",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.1rem",
  marginBottom: theme.spacing(1),
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between"
}));

const StyledListItemButton = styled(ListItemButton)(({ theme, active }) => ({
  borderRadius: theme.spacing(1),
  margin: theme.spacing(0.5, 2),
  backgroundColor: active ? "#1ABC9C" : "transparent",
  color: active ? "white" : "#9ca3af",
  "&:hover": {
    backgroundColor: active ? "#1ABC9C" : "#2c3e50",
    color: "white",
  },
  "& .MuiListItemIcon-root": {
    color: active ? "white" : "#9ca3af",
    minWidth: "40px",
  },
  "& .MuiListItemText-primary": {
    fontSize: "0.875rem",
    fontWeight: active ? 600 : 400,
  },
}));

const DashboardButton = styled(Box)(({ theme, active }) => ({
  borderRadius: theme.spacing(1),
  margin: theme.spacing(0.5, 2),
  backgroundColor: active ? "#1ABC9C" : "transparent",
  color: active ? "white" : "#9ca3af",
  display: "flex",
  alignItems: "center",
  "&:hover": {
    backgroundColor: active ? "#1ABC9C" : "#2c3e50",
    color: "white",
  },
}));

// ─── Component ────────────────────────────────────────────────────────────────

const SideDrawer = ({
  open,
  toggleDrawer,
  selectedView,
  handleViewChange,
  isReportsView,
  isVehicleCountView,
  isVehicleReportsView,
  isIntrusionView,
  cardOrder = [],
  currentPage = 0,
  // Boom barrier props
  boomBarrierAlerts = [],
  tokenAuth = null,
  isAdmin = true,
}) => {
  const [dashboardOpen, setDashboardOpen] = useState(true);

  const getIconComponent = (iconName) => iconComponents[iconName] || SecurityIcon;

  const handleDashboardToggle = (e) => {
    e.stopPropagation();
    setDashboardOpen(!dashboardOpen);
  };

  const handleDashboardClick = () => handleViewChange('home', null);

  const handleHomeClick = () => {
    window.open('https://cclai.in/dashboard-analytics-k.php', '_blank');
  };

  const handleIntrusionRedirect = () => {
    const token = getCurrentToken();
    const baseUrl = 'https://vaconfig.cclai.in/VA_Configurator/';
    window.open(token ? `${baseUrl}?token=${encodeURIComponent(token)}` : baseUrl, '_blank');
  };

  const isDashboardActive = !isReportsView && !isVehicleCountView && !isVehicleReportsView && !isIntrusionView;

  const CARDS_PER_PAGE = 6;
  const orderedDetectionTypes = cardOrder
    .map(id => DETECTION_TYPES.find(t => t.id === id))
    .filter(Boolean);

  const currentPageCards = orderedDetectionTypes.slice(
    currentPage * CARDS_PER_PAGE,
    (currentPage + 1) * CARDS_PER_PAGE
  );

  const totalPages = Math.ceil(orderedDetectionTypes.length / CARDS_PER_PAGE);

  return (
    <StyledDrawer variant="permanent" open={open}>
      {/* ── Header ── */}
      <Toolbar sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
        {open && (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
            <img
              src="bccl logo.png"
              alt="BCCL Logo"
              style={{
                width: "190px",
                height: "60px",
                borderRadius: "0.5rem",
                paddingLeft: "1vw",
                paddingTop: "1vh"
              }}
            />
          </Box>
        )}
        <IconButton onClick={toggleDrawer}>
          <ChevronLeftIcon sx={{ color: 'white', marginRight: '-1vw' }} />
        </IconButton>
      </Toolbar>

      <Divider sx={{ backgroundColor: "#2c3e50" }} />

      <List component="nav" sx={{ flexGrow: 1, px: 0 }}>

        {/* ── Dashboard Section Label ── */}
        {open && (
          <SectionTitle>
            Dashboard
          </SectionTitle>
        )}

        {/* ── Home Button ── */}
        <Box
          onClick={handleHomeClick}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            margin: "0.75rem 1rem",
            padding: "1rem 1rem",
            backgroundColor: "#1ABC9C",
            borderRadius: "0.5rem",
            color: "white",
            transition: "all 0.3s ease",
            "&:hover": {
              backgroundColor: "#16a085",
            },
          }}
        >
          <HomeIcon sx={{ marginRight: open ? "0.5rem" : "0" }} />
          {open && (
            <Typography sx={{ fontSize: "0.95rem", fontWeight: 600 }}>
              CCL-Home
            </Typography>
          )}
        </Box>

        {/* ── Live Dashboard Button ── */}
        <DashboardButton active={isDashboardActive}>
          <Box
            onClick={handleDashboardClick}
            sx={{
              display: "flex",
              alignItems: "center",
              flexGrow: 1,
              cursor: "pointer",
              padding: "8px 16px",
            }}
          >
            <ListItemIcon sx={{
              color: isDashboardActive ? "white" : "#9ca3af",
              minWidth: "40px",
            }}>
              <DashboardIcon />
            </ListItemIcon>
            <ListItemText
              primary="Live Dashboard"
              primaryTypographyProps={{
                fontSize: "0.875rem",
                fontWeight: isDashboardActive ? 600 : 400,
              }}
            />
          </Box>

          {open && (
            <IconButton
              onClick={handleDashboardToggle}
              size="small"
              sx={{
                color: isDashboardActive ? "white" : "#9ca3af",
                marginRight: "8px",
              }}
            >
              {dashboardOpen ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          )}
        </DashboardButton>

        {/* ── Cards from CURRENT PAGE (collapsible) ── */}
        {open && (
          <Collapse in={dashboardOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding sx={{ pl: 1 }}>
              {currentPageCards.map((type) => {
                const IconComponent = getIconComponent(type.icon);
                return (
                  <StyledListItemButton
                    key={type.id}
                    onClick={() => handleViewChange('live', type.id)}
                    sx={{ pl: 4, py: 0.5, minHeight: '40px', fontSize: '0.8rem' }}
                  >
                    <ListItemIcon sx={{ minWidth: '35px' }}>
                      <IconComponent sx={{ fontSize: '1.1rem' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={type.title}
                      primaryTypographyProps={{
                        fontSize: '0.8rem',
                        sx: {
                          whiteSpace: 'normal',
                          lineHeight: 1.2,
                          wordBreak: 'break-word',
                        },
                      }}
                    />
                  </StyledListItemButton>
                );
              })}

              {/* Card count + page info */}
              <Box sx={{ px: 4, py: 1 }}>
                <Chip
                  label={`${currentPageCards.length} Cards`}
                  size="small"
                  sx={{
                    backgroundColor: "#1ABC9C",
                    color: "white",
                    fontSize: "0.7rem",
                    height: "24px"
                  }}
                />
                {totalPages > 1 && (
                  <Typography variant="caption" sx={{
                    display: 'block',
                    mt: 1,
                    color: '#637592',
                    fontSize: '0.7rem'
                  }}>
                    Page {currentPage + 1} of {totalPages}
                  </Typography>
                )}
              </Box>
            </List>
          </Collapse>
        )}

        {/* Boom barriers moved to the GPS-RFID Alerts card in the VTS section. */}

        {/* ── Analytics ── */}
        {open && <SectionTitle sx={{ marginTop: 2 }}>Analytics</SectionTitle>}
        <StyledListItemButton
          onClick={() => handleViewChange('reports')}
          active={isReportsView}
        >
          <ListItemIcon>
            <AssessmentIcon />
          </ListItemIcon>
          <ListItemText primary="Reports & Analytics" />
        </StyledListItemButton>

        {/* ── Configuration ── */}
        {open && <SectionTitle sx={{ marginTop: 2 }}>Configuration</SectionTitle>}
        <StyledListItemButton
          onClick={handleIntrusionRedirect}
          active={false}
        >
          <ListItemIcon>
            <SettingsIcon />
          </ListItemIcon>
          <ListItemText primary="VA Config Manager" />
        </StyledListItemButton>

      </List>
    </StyledDrawer>
  );
};

export default SideDrawer;